import {useState, useEffect, useCallback} from "react";
import { Settings, X } from "lucide-react";

const COOKIE_KEY = "user-consent";
const DEFAULT_CONSENT = { necessary: true, analytics: false, marketing: false, functional: false };

export default function CookieConsent() {
    const [isOpen, setIsOpen] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [consent, setConsent] = useState(DEFAULT_CONSENT);

    const loadGoogleAnalytics = useCallback(() => {
        if (!consent.analytics) return;
        if (document.getElementById("ga-script")) return;

        const script = document.createElement("script");
        script.src = "https://www.googletagmanager.com/gtag/js?id=G-Y7LWV4MLM0";
        script.async = true;
        script.id = "ga-script";
        document.head.appendChild(script);

        const inlineScript = document.createElement("script");
        inlineScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-Y7LWV4MLM0');
        `;
        document.head.appendChild(inlineScript);
    }, [consent.analytics]);

    const applyConsent = useCallback((consent) => {
        document.cookie = `analytics=${consent.analytics}; path=/; max-age=31536000; Secure; SameSite=Lax`;
        document.cookie = `marketing=${consent.marketing}; path=/; max-age=31536000; Secure; SameSite=Lax`;
        document.cookie = `functional=${consent.functional}; path=/; max-age=31536000; Secure; SameSite=Lax`;
        if (consent.analytics) {
            loadGoogleAnalytics();
        }
    }, [loadGoogleAnalytics]);

    useEffect(() => {
        const storedConsent = localStorage.getItem(COOKIE_KEY);
        if (storedConsent) {
            const parsedConsent = JSON.parse(storedConsent);
            setConsent(parsedConsent);
            setIsOpen(false);
            applyConsent(parsedConsent);
        }
    }, [applyConsent]);

    useEffect(() => {
        applyConsent(consent);
    }, [consent, applyConsent]);

    const handleAccept = () => {
        const newConsent = { necessary: true, analytics: true, marketing: true, functional: true };
        setConsent(newConsent);
        localStorage.setItem(COOKIE_KEY, JSON.stringify(newConsent));
        applyConsent(newConsent);
        setIsOpen(false);
    };

    const handleDecline = () => {
        setConsent(DEFAULT_CONSENT);
        localStorage.setItem(COOKIE_KEY, JSON.stringify(DEFAULT_CONSENT));
        applyConsent(DEFAULT_CONSENT);
        setIsOpen(false);
    };

    const handleCustomize = () => {
        localStorage.setItem(COOKIE_KEY, JSON.stringify(consent));
        applyConsent(consent);
        setShowSettings(false);
    };



    return (
        <>
            {(isOpen || showSettings) && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-zinc-900 rounded-lg p-8 max-w-2xl w-full text-center shadow-xl mx-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => { setIsOpen(false); setShowSettings(false); }}
                                className="top-2 right-2 text-white hover:text-gray-400"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <h3 className="text-3xl font-bold mb-6 text-zinc-100">Wir verwenden Cookies</h3>
                        {!showSettings ? (
                            <>
                                <p className="mb-6 text-zinc-400">Diese Website nutzt Cookies, um Ihre Erfahrung zu verbessern. Sie können Ihre Einstellungen anpassen oder alle Cookies akzeptieren.</p>
                                <ul className="list-disc list-inside text-left text-zinc-400 space-y-1">
                                    <li><strong>Notwendige Cookies</strong> – Essenziell für den Betrieb der Website.</li>
                                    <li><strong>Analytische Cookies</strong> – Helfen uns, die Website zu verbessern (Google Analytics über den Tag Manager).</li>
                                    <li><strong>Marketing Cookies</strong> – Ermöglichen personalisierte Werbung.</li>
                                    <li><strong>Funktionale Cookies</strong> – Speichern Ihre Einstellungen.</li>
                                </ul>
                                <p className="mt-4 text-sm text-zinc-500">Mehr Informationen finden Sie in unserer <a href="/legal/privacy-policy" className="underline">Datenschutzerklärung</a> und <a href="/legal/privacy-policy" className="underline">Cookie-Richtlinie</a>.</p>
                                <div className="flex justify-center space-x-4 mt-6">
                                    <button onClick={handleAccept} className="bg-green-500 px-6 py-2 rounded transition-transform duration-300 hover:scale-105">Alles akzeptieren</button>
                                    <button onClick={() => setShowSettings(true)} className="bg-blue-500 px-6 py-2 rounded transition-transform duration-300 hover:scale-105">Einstellungen</button>
                                    <button onClick={handleDecline} className="bg-gray-500 px-6 py-2 rounded transition-transform duration-300 hover:scale-105">Ablehnen</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-lg font-semibold mb-4 text-zinc-100">Cookie Einstellungen</h2>
                                <div className="space-y-3 text-left">
                                    <label className="flex items-center text-zinc-400">
                                        <input type="checkbox" disabled checked className="mr-2" />
                                        Notwendige Cookies (immer aktiv)
                                    </label>
                                    <label className="flex items-center text-zinc-400">
                                        <input type="checkbox" checked={consent.analytics} onChange={() => setConsent(prev => ({ ...prev, analytics: !prev.analytics }))} className="mr-2" />
                                        Analytische Cookies (Google Analytics über Tag Manager)
                                    </label>
                                    <label className="flex items-center text-zinc-400">
                                        <input type="checkbox" checked={consent.marketing} onChange={() => setConsent(prev => ({ ...prev, marketing: !prev.marketing }))} className="mr-2" />
                                        Marketing Cookies (Werbung)
                                    </label>
                                    <label className="flex items-center text-zinc-400">
                                        <input type="checkbox" checked={consent.functional} onChange={() => setConsent(prev => ({ ...prev, functional: !prev.functional }))} className="mr-2" />
                                        Funktionale Cookies
                                    </label>
                                </div>
                                <div className="flex justify-end space-x-2 mt-4">
                                    <button onClick={() => setShowSettings(false)} className="bg-gray-500 px-4 py-2 rounded">Zurück</button>
                                    <button onClick={handleCustomize} className="bg-blue-500 px-4 py-2 rounded">Speichern</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <button
                onClick={() => { setIsOpen(true); setShowSettings(true); }}
                className="fixed bottom-4 left-4 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 z-50"
            >
                <Settings size={24} />
            </button>
        </>
    );
}